'use client';

import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { ArrowLeft, Search, RefreshCw, Loader2 } from '@/components/ui/lucide-memo';
import { DataAnnotationDetailView } from '@/components/portal/DataAnnotationDetailView';
import { InventoryFilterControls, type InventoryFilterOption } from '@/components/portal/InventoryFilterControls';
import { ViewSection } from '@/components/portal/ViewSection';
import { useKernel } from '@/kernel/Kernel';

const FONT_FAMILY = 'ui-sans-serif, -apple-system, system-ui, sans-serif';
const FONT_STYLE = { fontFamily: FONT_FAMILY } as const;

type AnnotationStats = {
  evaluatedByYou: number;
  evaluatedByAnyone: number;
  totalPrompts: number;
};

type FilterOption = { id: string; name: string };

type ListRow = {
  id: string;
  prompt: string;
  agentPrompt: string | null;
  ratedByOthers: boolean;
  hasReferenceImages: boolean;
  userId: string | null;
  organisationId: string | null;
  userName: string;
  organisationName: string;
  beforeImageUrl: string | null;
  afterImageUrl: string | null;
};

type FilterPayload = {
  users: FilterOption[];
  orgs: FilterOption[];
};

const PAGE_SIZE = 40;

const GRID_CLASS = 'grid grid-cols-[80px_2fr_1fr_1.2fr_0.6fr_0.8fr] items-center';

const MemoSearch = memo(Search);
const MemoRefreshCw = memo(RefreshCw);
const MemoArrowLeft = memo(ArrowLeft);
const MemoLoader2 = memo(Loader2);

const LazyImage = memo(({ src, alt }: { src: string | null; alt: string }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!src) return;
    const node = containerRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [src]);

  const handleLoad = useCallback(() => setIsLoaded(true), []);
  const handleError = useCallback(() => setHasError(true), []);

  return (
    <div ref={containerRef} className="relative h-full w-full">
      {!src ? (
        <div className="h-full w-full flex items-center justify-center text-[10px] text-white/30">-</div>
      ) : (
        <>
          {shouldLoad && !hasError ? (
            <img
              src={src}
              alt={alt}
              className="h-full w-full object-contain"
              loading="lazy"
              onLoad={handleLoad}
              onError={handleError}
            />
          ) : null}
          {!shouldLoad || (!isLoaded && !hasError) ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-3 w-3 rounded-full border border-white/30 border-t-white/80 animate-spin" />
            </div>
          ) : null}
          {hasError ? (
            <div className="absolute inset-0 flex items-center justify-center text-[10px] text-white/30">Failed</div>
          ) : null}
        </>
      )}
    </div>
  );
});
LazyImage.displayName = 'LazyImage';

const AnnotationRow = memo(({ row, onSelect }: { row: ListRow; onSelect: (id: string) => void }) => {
  const handleClick = useCallback(() => onSelect(row.id), [onSelect, row.id]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className={clsx(
        GRID_CLASS,
        'h-16 w-full text-left border-b border-[#2A2A2A]/50 text-sm text-white transition-colors hover:bg-white/[0.04] cursor-pointer'
      )}
      style={FONT_STYLE}
    >
      <div className="h-12 w-full overflow-hidden border-r border-[#2A2A2A]/50 px-2 flex items-center">
        <div className="h-10 w-full rounded overflow-hidden">
          <LazyImage src={row.afterImageUrl} alt="After" />
        </div>
      </div>
      <div className="border-r border-[#2A2A2A]/50 px-4 text-[13px] text-white/80 leading-snug line-clamp-2">{row.prompt || '-'}</div>
      <div className="border-r border-[#2A2A2A]/50 px-4 text-[13px] text-white/60 line-clamp-2">{row.agentPrompt?.trim() || '-'}</div>
      <div className="border-r border-[#2A2A2A]/50 px-4 text-[13px] text-white/60">{row.userName} ({row.organisationName})</div>
      <div className="border-r border-[#2A2A2A]/50 px-4 text-[13px] text-white/50">{row.ratedByOthers ? 'Yes' : 'No'}</div>
      <div className="px-4 text-[13px] text-white/50">{row.hasReferenceImages ? 'Yes' : 'No'}</div>
    </button>
  );
});
AnnotationRow.displayName = 'AnnotationRow';

export const DataAnnotationView = memo(() => {
  const { kernel } = useKernel();
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string | null>(null);
  const [rows, setRows] = useState<ListRow[]>([]);
  const [stats, setStats] = useState<AnnotationStats>({ evaluatedByYou: 0, evaluatedByAnyone: 0, totalPrompts: 0 });
  const [filters, setFilters] = useState<FilterPayload>({ users: [], orgs: [] });
  const [userFilter, setUserFilter] = useState('');
  const [orgFilter, setOrgFilter] = useState('');
  const [promptFilter, setPromptFilter] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadList = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const params = new URLSearchParams();
      if (userFilter) params.set('userId', userFilter);
      if (orgFilter) params.set('orgId', orgFilter);
      if (promptFilter) params.set('prompt', promptFilter);
      params.set('page', String(page));
      params.set('pageSize', String(PAGE_SIZE));
      const response = await fetch(`/api/annotation/list?${params.toString()}`, { cache: 'no-store' });
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
      const data = await response.json();
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setStats({
        evaluatedByYou: Number(data.stats?.evaluatedByYou ?? 0),
        evaluatedByAnyone: Number(data.stats?.evaluatedByAnyone ?? 0),
        totalPrompts: Number(data.stats?.totalPrompts ?? 0),
      });
      setFilters({
        users: Array.isArray(data.filters?.users) ? data.filters.users : [],
        orgs: Array.isArray(data.filters?.orgs) ? data.filters.orgs : [],
      });
      const responsePage = Number(data.page ?? page);
      const responseTotalRows = Number(data.totalRows ?? 0);
      if (Number.isFinite(responsePage) && responsePage !== page) setPage(responsePage);
      setTotalRows(Number.isFinite(responseTotalRows) ? responseTotalRows : 0);
    } catch (error) {
      console.error('[DataAnnotationView] Failed to load list', error);
      setRows([]);
      setErrorMessage('Unable to load prompt list.');
    } finally {
      setIsLoading(false);
    }
  }, [orgFilter, page, promptFilter, userFilter]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const handleRowClick = useCallback((id: string) => {
    setSelectedSnapshotId(id);
  }, []);

  const handleBackFromDetail = useCallback(() => {
    setSelectedSnapshotId(null);
    void loadList();
  }, [loadList]);

  const handleBack = useCallback(() => {
    const k = kernel.current;
    if (!k) return;
    const sessionId = k.getActiveChatSessionId();
    if (!sessionId) return;
    const moved = k.goToPreviousSubject(sessionId);
    if (!moved) {
      k.setActiveSubject(sessionId, null);
    }
  }, [kernel]);

  const handleToggleSearch = useCallback(() => {
    if (isSearchOpen || promptFilter.trim().length > 0) {
      setPromptFilter('');
      setIsSearchOpen(false);
      setPage(1);
      return;
    }
    setIsSearchOpen(true);
  }, [isSearchOpen, promptFilter]);

  const handleSearchChange = useCallback((val: string) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setPromptFilter(val);
      setPage(1);
    }, 350);
  }, []);

  const handleManualRefresh = useCallback(() => {
    void loadList();
  }, [loadList]);

  const handleSearchInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    handleSearchChange(event.target.value);
  }, [handleSearchChange]);

  const handleSearchKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Escape') return;
    setPromptFilter('');
    setIsSearchOpen(false);
    setPage(1);
  }, []);

  const handleFirstPage = useCallback(() => setPage(1), []);
  const handlePreviousPage = useCallback(() => setPage((p) => Math.max(1, p - 1)), []);

  const filterOptions = useMemo<InventoryFilterOption[]>(() => [
    { key: 'userId', label: 'User', values: filters.users.map((u) => ({ value: u.id, label: u.name })) },
    { key: 'orgId', label: 'Organisation', values: filters.orgs.map((o) => ({ value: o.id, label: o.name })) },
  ], [filters]);

  const viewFilters = useMemo(
    () => ({ userId: userFilter || undefined, orgId: orgFilter || undefined }),
    [orgFilter, userFilter],
  );

  const isFilterKeyLocked = useCallback(() => false, []);
  const mergeWithLockedFilters = useCallback((candidate: Record<string, unknown>) => candidate, []);

  const handleSetFilters = useCallback((next: Record<string, unknown>) => {
    setUserFilter(typeof next.userId === 'string' ? next.userId : '');
    setOrgFilter(typeof next.orgId === 'string' ? next.orgId : '');
    setPage(1);
  }, []);

  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
  const handleNextPage = useCallback(() => setPage((p) => Math.min(totalPages, p + 1)), [totalPages]);
  const handleLastPage = useCallback(() => setPage(totalPages), [totalPages]);
  const canGoPrevious = page > 1 && !isLoading;
  const canGoNext = page < totalPages && !isLoading;
  const isSearchVisible = isSearchOpen || promptFilter.trim().length > 0;

  if (selectedSnapshotId) {
    return (
      <DataAnnotationDetailView
        snapshotId={selectedSnapshotId}
        userFilter={userFilter}
        orgFilter={orgFilter}
        promptFilter={promptFilter}
        onBack={handleBackFromDetail}
      />
    );
  }

  return (
    <ViewSection
      contentClassName="!px-0 !py-0 !space-y-0"
      hideViewLabel={true}
    >
      {/* Breadcrumb header — matches assets tab */}
      <div
        className="h-[56px] border-b border-[#2A2A2A] px-4"
        style={FONT_STYLE}
      >
        <div className="flex h-full items-center gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex h-[24px] w-[24px] items-center justify-center text-white transition-colors hover:text-white"
            aria-label="Back"
            title="Back"
          >
            <MemoArrowLeft size={16} strokeWidth={2.4} />
          </button>
          <span className="h-5 w-[2px] bg-white/28" />
          <div className="flex min-w-0 flex-col gap-[2px] justify-center">
            <span className="text-[14px] leading-[14px] font-normal text-white/78">
              Admin / Data
            </span>
            <h1 className="text-[14px] font-normal leading-[14px] tracking-normal text-white">
              Annotation snapshots
            </h1>
          </div>
          <div className="flex items-center gap-3 ml-6 text-[13px] text-white/45" style={FONT_STYLE}>
            <span>{stats.evaluatedByYou} by you</span>
            <span className="h-3 w-px bg-white/15" />
            <span>{stats.evaluatedByAnyone} by anyone</span>
            <span className="h-3 w-px bg-white/15" />
            <span>{stats.totalPrompts} total</span>
          </div>
        </div>
      </div>

      {/* Toolbar — matches assets tab */}
      <div className="relative z-20 mt-0 px-4 py-3">
        <div className="flex w-full flex-wrap items-center gap-0">
          <div className="mr-1 flex items-center flex-row-reverse">
            <button
              type="button"
              onClick={handleToggleSearch}
              className="inline-flex h-8 w-8 items-center justify-center rounded-[9px] border border-transparent bg-transparent text-white/75 transition-colors hover:bg-[#222222] hover:text-white"
              aria-label="Toggle search"
              title="Search"
            >
              <MemoSearch size={16} strokeWidth={2.1} />
            </button>
            <button
              type="button"
              onClick={handleManualRefresh}
              disabled={isLoading}
              className="mr-1 inline-flex h-8 w-8 items-center justify-center rounded-[9px] border border-transparent bg-transparent text-white/75 transition-colors hover:bg-[#222222] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Refresh"
              title="Refresh"
            >
              <MemoRefreshCw size={15} strokeWidth={2.1} className={isLoading ? 'animate-spin' : undefined} />
            </button>
            <div
              className={clsx(
                'overflow-hidden transition-[max-width,opacity,margin] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
                isSearchVisible ? 'mr-1 max-w-[220px] opacity-100' : 'mr-0 max-w-0 opacity-0',
              )}
            >
              <div className="relative h-8 w-[220px] bg-[#181818]">
                <MemoSearch className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-white/45" size={14} />
                <input
                  type="text"
                  placeholder="Type to search prompts..."
                  className="h-8 w-full appearance-none rounded-none border-0 bg-transparent pl-7 pr-2 text-[13px] text-white/85 shadow-none outline-none placeholder:text-white/35 focus:outline-none"
                  defaultValue={promptFilter}
                  onChange={handleSearchInputChange}
                  onKeyDown={handleSearchKeyDown}
                />
              </div>
            </div>
          </div>

          <InventoryFilterControls
            filterOptions={filterOptions}
            viewFilters={viewFilters}
            isFilterKeyLocked={isFilterKeyLocked}
            mergeWithLockedFilters={mergeWithLockedFilters}
            setFilters={handleSetFilters}
          />

          {errorMessage && <span className="ml-2 text-[13px] text-white/40">{errorMessage}</span>}
        </div>
      </div>

      {/* Table — matches assets tab */}
      <div className="flex-1 flex flex-col min-h-[500px]">
        <div className="w-full flex flex-col overflow-hidden rounded-none border border-[#2A2A2A] bg-[#181818]">
          {/* Table header */}
          <div
            className={clsx(
              'sticky top-0 z-10',
              GRID_CLASS,
              'h-10 bg-[#1D1D1D] border-b border-[#2A2A2A] text-[13px] font-normal normal-case tracking-normal text-white/55 backdrop-blur-none'
            )}
            style={FONT_STYLE}
          >
            <div className="flex h-10 items-center border-r border-[#2A2A2A] px-4">Image</div>
            <div className="flex h-10 items-center border-r border-[#2A2A2A] px-4">Prompt</div>
            <div className="flex h-10 items-center border-r border-[#2A2A2A] px-4">Agent prompt</div>
            <div className="flex h-10 items-center border-r border-[#2A2A2A] px-4">User (Org)</div>
            <div className="flex h-10 items-center border-r border-[#2A2A2A] px-4">Rated</div>
            <div className="flex h-10 items-center px-4">Ref images</div>
          </div>

          {/* Rows */}
          <div className="flex-1">
            {isLoading && rows.length === 0 && (
              <div className="p-12 flex flex-col items-center justify-center gap-4 text-white/20">
                <MemoLoader2 size={32} className="animate-spin" />
                <span className="text-sm font-medium">Loading snapshots...</span>
              </div>
            )}
            {!isLoading && rows.length === 0 && (
              <div className="p-12 flex items-center justify-center text-white/30 text-[13px]">
                No prompts found.
              </div>
            )}
            {rows.map((row) => (
              <AnnotationRow key={row.id} row={row} onSelect={handleRowClick} />
            ))}
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 px-4 py-3" style={FONT_STYLE}>
            <button
              type="button"
              onClick={handleFirstPage}
              disabled={!canGoPrevious}
              className={clsx('px-2 py-1 text-[13px] text-white/50 hover:text-white transition-colors', !canGoPrevious && 'opacity-30 cursor-not-allowed')}
            >
              «
            </button>
            <button
              type="button"
              onClick={handlePreviousPage}
              disabled={!canGoPrevious}
              className={clsx('px-2 py-1 text-[13px] text-white/50 hover:text-white transition-colors', !canGoPrevious && 'opacity-30 cursor-not-allowed')}
            >
              ‹
            </button>
            <span className="px-3 text-[13px] text-white/60">{page} / {totalPages}</span>
            <button
              type="button"
              onClick={handleNextPage}
              disabled={!canGoNext}
              className={clsx('px-2 py-1 text-[13px] text-white/50 hover:text-white transition-colors', !canGoNext && 'opacity-30 cursor-not-allowed')}
            >
              ›
            </button>
            <button
              type="button"
              onClick={handleLastPage}
              disabled={!canGoNext}
              className={clsx('px-2 py-1 text-[13px] text-white/50 hover:text-white transition-colors', !canGoNext && 'opacity-30 cursor-not-allowed')}
            >
              »
            </button>
          </div>
        )}
      </div>
    </ViewSection>
  );
});

DataAnnotationView.displayName = 'DataAnnotationView';
